import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { bmrMifflin, bmi as calcBmi, checkBody, goalTargets, displayTargets, weeklyNote, MIN_CALORIES } from "./calorie";
import { NoticeBox } from "@/components/NoticeBox";
import { zigzagPlan, cycleSteps } from "./calorieCycle";
import { reportTable } from "@/lib/calc/report";
import type { TableColumn } from "@/lib/calc/table";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));

const ACTIVITY_LEVELS = [
  { value: "1.2", label: "Sedentary" },
  { value: "1.375", label: "Lightly Active" },
  { value: "1.55", label: "Moderately Active" },
  { value: "1.725", label: "Very Active" },
  { value: "1.9", label: "Extremely Active" },
];

const TOOL_INFO_CONTENT = {
  what: `Estimates your daily calorie needs to maintain, lose, or gain weight, based on your body stats, activity level, and goal.`,
  how: `Enter your stats and activity level, choose your goal (maintain, lose, or gain), and your estimated daily calorie target is calculated.`,
  tip: `Calorie needs are an estimate, not an exact number — treat the result as a starting point to adjust from based on how your actual weight trends over a few weeks, rather than a precise target to hit exactly every day.`,
};

export default function CalorieCalculator() {
  const [units, setUnits] = useState<"metric" | "imperial">("metric");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [age, setAge] = useState("30");
  const [heightCm, setHeightCm] = useState("175");
  const [weightKg, setWeightKg] = useState("75");
  const [heightFt, setHeightFt] = useState("5");
  const [heightIn, setHeightIn] = useState("9");
  const [weightLb, setWeightLb] = useState("165");
  const [activity, setActivity] = useState("1.375");

  const a = parseNumber(age) ?? 0;
  const h = units === "metric" ? parseNumber(heightCm) ?? 0 : (parseNumber(heightFt) ?? 0) * 30.48 + (parseNumber(heightIn) ?? 0) * 2.54;
  const w = units === "metric" ? parseNumber(weightKg) ?? 0 : (parseNumber(weightLb) ?? 0) * 0.453592;
  const check = checkBody(a, h, w);
  const tdee = bmrMifflin(gender, w, h, a) * (Number(activity) || 1.2);
  const goals = check.error ? [] : displayTargets(goalTargets(tdee, gender, check.underweight));
  const floor = MIN_CALORIES[gender];

  const { blocked, gate } = useLogToolUsage(true, 3000);
  const [cycleOn, setCycleOn] = useState(false);
  const [cycleDays, setCycleDays] = useState<"1" | "2" | "3">("2");
  const [cycleSwing, setCycleSwing] = useState("15");
  const [cycleGoal, setCycleGoal] = useState("");
  const cycleChoices = goals.filter(g => !g.unavailable);
  const cycleBase = cycleChoices.find(g => g.shownLabel === cycleGoal) ?? cycleChoices.find(g => g.kind === "maintain");
  const plan = useMemo(() => (cycleOn && cycleBase ? zigzagPlan(Math.round(cycleBase.kcal), floor, Number(cycleDays) as 1 | 2 | 3, Number(cycleSwing) / 100) : null), [cycleOn, cycleBase, floor, cycleDays, cycleSwing]);
  const cycleColumns: TableColumn<{ day: string; kcal: number; high: boolean }>[] = [
    { key: "day", header: "Day", get: r => r.day },
    { key: "kcal", header: "Calories", get: r => r.kcal, format: r => `${r.kcal} cal`, align: "right" },
    { key: "type", header: "Type", get: r => (r.high ? "Higher" : "Lower") },
  ];


  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <Tabs value={units} onValueChange={v => setUnits(v as "metric" | "imperial")}>
          <TabsList className="w-full">
            <TabsTrigger value="metric" className="flex-1">Metric</TabsTrigger>
            <TabsTrigger value="imperial" className="flex-1">Imperial</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs value={gender} onValueChange={v => setGender(v as "male" | "female")}>
          <TabsList className="w-full">
            <TabsTrigger value="male" className="flex-1">Male</TabsTrigger>
            <TabsTrigger value="female" className="flex-1">Female</TabsTrigger>
          </TabsList>
        </Tabs>

        <div><Label>Age</Label><Input inputMode="decimal" aria-label="Age" value={age} onChange={e => setAge(e.target.value)} /></div>

        {units === "metric" ? (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Height (cm)</Label><Input inputMode="decimal" aria-label="Height (cm)" value={heightCm} onChange={e => setHeightCm(e.target.value)} /></div>
            <div><Label>Weight (kg)</Label><Input inputMode="decimal" aria-label="Weight (kg)" value={weightKg} onChange={e => setWeightKg(e.target.value)} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Height (ft)</Label><Input inputMode="decimal" aria-label="Height (ft)" value={heightFt} onChange={e => setHeightFt(e.target.value)} /></div>
            <div><Label>Height (in)</Label><Input inputMode="decimal" aria-label="Height (in)" value={heightIn} onChange={e => setHeightIn(e.target.value)} /></div>
            <div><Label>Weight (lb)</Label><Input inputMode="decimal" aria-label="Weight (lb)" value={weightLb} onChange={e => setWeightLb(e.target.value)} /></div>
          </div>
        )}

        <div>
          <Label>Activity Level</Label>
          <Select value={activity} onValueChange={setActivity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIVITY_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 pt-2 border-t">
          {check.error && <p className="text-sm text-red-600" data-testid="cal-error">{check.error}</p>}
          {blocked && !check.error && <p className="text-sm text-center text-muted-foreground py-2">— blocked at today's usage limit —</p>}
          {!blocked && check.underweight && (
            <NoticeBox className="text-xs" testId="cal-underweight">Your BMI is under 18.5, so this tool does not suggest weight-loss targets. Talk to a doctor or dietitian before changing how much you eat.</NoticeBox>
          )}
          {!blocked && goals.map(g => (
            <div key={g.label} className={`p-3 rounded-lg border ${g.label === "Maintain Weight" ? "bg-primary/5 border-primary/20" : "bg-card"} ${g.unavailable ? "opacity-70" : ""}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{g.shownLabel}</p>
                  <p className="text-xs text-muted-foreground">{g.unavailable ? "Not recommended for you" : weeklyNote(g)}</p>
                </div>
                <p className="text-base font-bold" data-testid={`cal-${g.label.toLowerCase().replace(/ /g, "-")}`}>{g.unavailable ? "—" : `${Math.round(g.kcal)} cal`}</p>
              </div>
              {g.mergedNote && <NoticeBox className="text-xs mt-1" testId="cal-merged">{g.mergedNote}</NoticeBox>}
              {g.clamped && <NoticeBox className="text-xs mt-1" testId="cal-clamped">Limited to a safe level (at most 25% below maintenance, never under {floor} cal). Eating less than this should only be done under medical supervision.</NoticeBox>}
            </div>
          ))}
          {!blocked && goals.some(g => g.kind === "loss" && g.unavailable) && !check.underweight && (
            <p className="text-xs text-muted-foreground" data-testid="cal-noloss">Your maintenance level is close to the safe minimum of {floor} cal, so a larger deficit is not suggested. Raising activity is a safer way to lose weight.</p>
          )}
          {!blocked && !check.error && cycleChoices.length > 0 && (
            <div className="pt-2 space-y-2" data-testid="cal-cycle">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cycleOn} onChange={e => setCycleOn(e.target.checked)} data-testid="cal-cycle-toggle" /> Spread the week as higher and lower days (calorie cycling)</label>
              {cycleOn && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label>Goal</Label>
                      <Select value={cycleBase?.shownLabel ?? ""} onValueChange={setCycleGoal}><SelectTrigger aria-label="Cycling goal"><SelectValue /></SelectTrigger>
                        <SelectContent>{cycleChoices.map(g => <SelectItem key={g.shownLabel} value={g.shownLabel}>{g.shownLabel}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Higher days</Label>
                      <Select value={cycleDays} onValueChange={v => setCycleDays(v as "1" | "2" | "3")}><SelectTrigger aria-label="Higher days"><SelectValue /></SelectTrigger>
                        <SelectContent>{["1", "2", "3"].map(d => <SelectItem key={d} value={d}>{d} a week</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Swing</Label>
                      <Select value={cycleSwing} onValueChange={setCycleSwing}><SelectTrigger aria-label="Swing"><SelectValue /></SelectTrigger>
                        <SelectContent>{["10", "15", "20"].map(d => <SelectItem key={d} value={d}>+{d}%</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  {plan && !plan.ok && <NoticeBox className="text-xs" testId="cal-cycle-refused">{plan.reason}</NoticeBox>}
                  {plan?.ok && (
                    <>
                      {plan.limitedByFloor && <NoticeBox className="text-xs" testId="cal-cycle-limited">The swing was reduced to {Math.round(plan.appliedSwing * 100)}% so that lower days stay at or above {floor} cal.</NoticeBox>}
                      <div className="grid grid-cols-7 gap-1 text-center" data-testid="cal-cycle-week">
                        {plan.days.map(d => <div key={d.day} className={`p-1 rounded border text-xs ${d.high ? "bg-primary/10 border-primary/30" : "bg-card"}`}><p className="text-muted-foreground">{d.day}</p><p className="font-semibold">{d.kcal}</p></div>)}
                      </div>
                      <p className="text-xs text-muted-foreground text-center" data-testid="cal-cycle-total">Week total {plan.weeklyTotal} cal, the same as {Math.round(plan.targetKcal)} cal every day. Cycling is a way to schedule the same weekly calories; it is not shown to work better than a flat target.</p>
                      <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
                        <CalcReport
                          title="Weekly calorie plan" tool="calorie-calculator" gate={gate} filename="calorie-week.csv" steps={cycleSteps(plan)}
                          summary={`${Math.round(plan.targetKcal)} cal a day as ${plan.highDays} higher day(s) at ${plan.highKcal} and ${7 - plan.highDays} lower at ${plan.lowKcal}; week ${plan.weeklyTotal} cal`}
                          notes={["Maintenance from the Mifflin-St Jeor equation (Am J Clin Nutr 1990;51:241-247) times the activity factor chosen.", `Safe minimum of ${floor} cal is this tool's long-standing floor; it was not re-checked against a published guideline. Not medical advice.`]}
                          tables={[reportTable({ caption: "Weekly plan", heading: "Weekly plan", rows: plan.days, columns: cycleColumns, rowKey: r => r.day })]}
                        />
                      </Suspense>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {!check.error && <p className="text-xs text-muted-foreground text-center">BMI {calcBmi(w, h).toFixed(1)}</p>}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Estimate based on the Mifflin-St Jeor equation. Individual needs vary — consult a doctor or dietitian for personalized guidance.
        </p>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
